import { useEffect, useState } from 'react'
import { kickoffWithDate } from '../../shared/clock'
import { buildRecap, type RecapBoard } from '../../shared/recap'
import { api, type SeasonRow, type SeasonWeek, type State } from '../api'
import { Empty } from '../ui'

/*
 * The week as something you can paste.
 *
 * The family already has a group thread and everybody reads it. Push has never
 * been confirmed arriving on a phone, and SMS from the Worker means carrier
 * registration, an opt-out obligation and a number nobody recognises. Lines the
 * commissioner pastes himself cost nothing and are informal because they are
 * genuinely him sending them.
 *
 * EVERY NUMBER COMES FROM THE SEASON ENDPOINT, none of it is scored here. That
 * endpoint already applies the missing-pick cap per week, so `byWeek` is the
 * same number the Season table shows and the two cannot disagree. The first
 * version of this screen re-scored the board locally, which was a second
 * derivation of a fact that already existed - the exact habit that produced
 * four outages in one day (docs/STATE.md).
 *
 * THE DEFAULT WEEK IS THE LAST ONE WITH RESULTS, not the newest board. The
 * commissioner publishes Thursday's board and then wants to tell everyone how
 * last week went; defaulting to the board just published gave him an empty
 * table and a message with nothing in it.
 */
export function Recap({ state, tz }: { state: State; tz: string }) {
  const [weeks, setWeeks] = useState<SeasonWeek[] | null>(null)
  const [rows, setRows] = useState<SeasonRow[]>([])
  const [pick, setPick] = useState<string | null>(null)
  const [withSeason, setWithSeason] = useState(true)
  const [withBoard, setWithBoard] = useState(true)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    void api.season().then(
      (r) => {
        setWeeks(r.weeks)
        setRows(r.rows)
        const played = r.weeks.filter((w) => w.finals > 0)
        setPick((played.at(-1) ?? r.weeks.at(-1))?.id ?? null)
      },
      () => setWeeks([]),
    )
  }, [])

  if (weeks === null) return <Empty>Adding it up…</Empty>
  if (weeks.length === 0) return <Empty>No weeks to report on yet.</Empty>

  const weekId = pick ?? weeks.at(-1)!.id
  const week = weeks.find((w) => w.id === weekId) ?? weeks.at(-1)!

  /*
   * The board that is up NOW, offered only when it is a different week from
   * the one being recapped - otherwise the message announces the board whose
   * results it is already carrying.
   */
  const current = state.week
  const currentWeek = current ? weeks.find((w) => w.id === current.id) : undefined
  const board: RecapBoard | null =
    withBoard && current && current.id !== weekId
      ? {
          label: current.label,
          games: currentWeek?.games ?? state.games.length,
          // Only a deadline that has not passed. An invented or stale one in
          // the family thread is worse than none, because people believe it.
          closes:
            current.lockAt !== null && current.lockAt * 1000 > Date.now()
              ? kickoffWithDate(current.lockAt, tz)
              : null,
        }
      : null

  const text = buildRecap({
    label: week.label,
    complete: week.games > 0 && week.finals === week.games,
    players: rows.map((r) => ({
      name: r.name,
      weekPoints: r.byWeek[weekId] ?? 0,
      seasonPoints: r.total,
    })),
    withSeason,
    weeksPlayed: weeks.filter((w) => w.finals > 0).length,
    board,
    url: 'picks.teamcramer.com',
  })

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setSaid('Copied.')
    } catch {
      // The clipboard can refuse. The text above is selectable either way.
      setSaid('Could not copy — select the text above and copy it by hand.')
    }
  }

  const CHIP = 'slate-num slate-tap min-h-11 flex-1 rounded-[3px] text-[10px] font-bold tracking-[0.1em] uppercase'

  return (
    <div className="px-3 pb-5">
      <label className="mb-2.5 flex items-center justify-between gap-3 border border-hairline px-3 py-2.5">
        <span className="slate-num text-[9px] tracking-[0.1em] text-faint uppercase">Week</span>
        <select
          value={weekId}
          onChange={(e) => {
            setPick(e.target.value)
            setSaid(null)
          }}
          className="slate-num bg-ground px-2 py-2 text-[13px]"
        >
          {weeks.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
              {w.finals === 0 ? ' — no results yet' : w.finals < w.games ? ' — in play' : ''}
            </option>
          ))}
        </select>
      </label>

      <div className="mb-2.5 flex gap-2">
        <button
          type="button"
          onClick={() => setWithSeason(!withSeason)}
          aria-pressed={withSeason}
          className={[CHIP, withSeason ? 'bg-gold text-nightink' : 'border border-hairline bg-card text-slate'].join(' ')}
        >
          Season
        </button>
        {/* Offered only when there IS a newer board; a dead toggle is worse
            than an absent one. */}
        {current && current.id !== weekId && (
          <button
            type="button"
            onClick={() => setWithBoard(!withBoard)}
            aria-pressed={withBoard}
            className={[CHIP, withBoard ? 'bg-gold text-nightink' : 'border border-hairline bg-card text-slate'].join(' ')}
          >
            New board
          </button>
        )}
      </div>

      {/*
       * The real string, selectable, in a block that wraps rather than one that
       * reflows. What you see is exactly what leaves - a rendered table would
       * be a second version of the same facts and could disagree with the one
       * being sent.
       */}
      <pre className="slate-num mb-2.5 overflow-x-auto rounded-[4px] border border-hairline bg-card p-3 text-[12px] leading-[1.6] whitespace-pre-wrap text-ink">
        {text}
      </pre>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void copy()}
          className="slate-num slate-tap min-h-11 flex-1 rounded-[3px] bg-ink text-[11px] font-bold tracking-[0.12em] text-card uppercase"
        >
          Copy
        </button>
        {/*
         * An empty recipient with the body filled: Messages opens, you choose
         * the thread, you press send. Deliberately not a list of numbers -
         * nothing here is automated messaging, which is what keeps it outside
         * every registration and opt-out obligation in the first place.
         */}
        <a
          href={`sms:&body=${encodeURIComponent(text)}`}
          className="slate-num slate-tap flex min-h-11 flex-1 items-center justify-center rounded-[3px] border border-hairline bg-card text-[11px] font-bold tracking-[0.12em] text-slate uppercase"
        >
          Send &rarr;
        </a>
      </div>

      {said && <p className="mt-2 text-[13px] text-slate">{said}</p>}
    </div>
  )
}
